import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Sparkles, ArrowRight, TrendingUp, TrendingDown, Minus, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const THEME_NAMES = {
  love: 'Amour',
  career: 'Carrière',
  health: 'Santé',
  longevity: 'Longévité',
  finances: 'Finances',
  creativity: 'Créativité',
  spirituality: 'Spiritualité',
};

const ComparePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { older, newer, time_diff_days } = location.state || {};

  useEffect(() => {
    if (!older || !newer) {
      toast.error('Données de comparaison manquantes');
      navigate('/history');
    }
  }, [older, newer, navigate]);

  if (!older || !newer) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  // Find common themes
  const commonThemes = older.selected_themes?.filter(t => 
    newer.selected_themes?.includes(t)
  ) || [];

  // Get insights by theme
  const getInsightByTheme = (reading, theme) => {
    return reading.analysis_result?.insights?.find(i => i.theme === theme);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/history')}
            className="mb-6 rounded-full hover:bg-secondary/50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'historique
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <Sparkles className="h-8 w-8 text-primary" />
            <h1 className="font-heading text-3xl md:text-4xl">
              Comparaison temporelle
            </h1>
          </div>
          
          <div className="flex items-center gap-3 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{time_diff_days} jours d'évolution</span>
          </div>
        </motion.div>

        {/* Timeline Header */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-card p-6 rounded-2xl border border-border"
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <span className="w-3 h-3 rounded-full bg-blue-500" />
              Lecture ancienne
            </div>
            <p className="font-heading text-lg">{formatDate(older.created_at)}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {older.selected_themes?.map(theme => (
                <span key={theme} className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                  {THEME_NAMES[theme] || theme}
                </span>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-card p-6 rounded-2xl border border-border"
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <span className="w-3 h-3 rounded-full bg-green-500" />
              Lecture récente
            </div>
            <p className="font-heading text-lg">{formatDate(newer.created_at)}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {newer.selected_themes?.map(theme => (
                <span key={theme} className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                  {THEME_NAMES[theme] || theme}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* General Impressions Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="font-heading text-xl mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Évolution de l'impression générale
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-5 rounded-xl border border-blue-200">
              <p className="text-sm text-blue-900 leading-relaxed">
                {older.analysis_result?.general_impression || "Non disponible"}
              </p>
            </div>
            
            <div className="bg-green-50 p-5 rounded-xl border border-green-200">
              <p className="text-sm text-green-900 leading-relaxed">
                {newer.analysis_result?.general_impression || "Non disponible"}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Theme by Theme Comparison */}
        {commonThemes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="font-heading text-xl mb-6">
              Comparaison par thème
            </h2>
            
            <div className="space-y-6">
              {commonThemes.map((theme, index) => {
                const olderInsight = getInsightByTheme(older, theme);
                const newerInsight = getInsightByTheme(newer, theme);
                
                return (
                  <motion.div
                    key={theme}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="bg-card rounded-2xl border border-border overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-4 border-b border-border">
                      <h3 className="font-heading text-lg">
                        {THEME_NAMES[theme] || theme}
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2">
                      {/* Older Reading */}
                      <div className="p-5 border-r border-border">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="w-2 h-2 rounded-full bg-blue-500" />
                          <span className="text-xs font-semibold text-blue-600">AVANT</span>
                        </div>
                        
                        {olderInsight ? (
                          <div className="space-y-3">
                            <p className="text-sm font-medium">{olderInsight.title}</p>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {olderInsight.interpretation}
                            </p>
                            {olderInsight.advice && (
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-xs text-blue-800">{olderInsight.advice}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">Non analysé</p>
                        )}
                      </div>
                      
                      {/* Newer Reading */}
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="w-2 h-2 rounded-full bg-green-500" />
                          <span className="text-xs font-semibold text-green-600">MAINTENANT</span>
                        </div>
                        
                        {newerInsight ? (
                          <div className="space-y-3">
                            <p className="text-sm font-medium">{newerInsight.title}</p>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {newerInsight.interpretation}
                            </p>
                            {newerInsight.advice && (
                              <div className="bg-green-50 p-3 rounded-lg">
                                <p className="text-xs text-green-800">{newerInsight.advice}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">Non analysé</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* No Common Themes */}
        {commonThemes.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-card rounded-2xl border border-border"
          >
            <Minus className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              Ces deux lectures n'ont pas de thèmes en commun à comparer.
            </p>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 flex flex-wrap gap-4 justify-center"
        >
          <Button
            onClick={() => navigate('/scan')}
            size="lg"
            className="rounded-full bg-primary hover:bg-primary/90 px-8 py-6"
          >
            <Sparkles className="mr-2 h-5 w-5" />
            Nouvelle lecture
          </Button>
          <Button
            onClick={() => navigate('/history')}
            variant="outline"
            size="lg"
            className="rounded-full px-8 py-6"
          >
            Voir tout l'historique
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default ComparePage;
